import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import {
    LayoutDashboard,
    TrendingUp,
    Database,
    Sparkles,
    MessageSquare,
    Users,
    Rocket,
    Settings,
    Edit2,
    ChevronDown,
    ChevronRight,
    LogOut,
    StickyNote,
    Layout,
    Globe,
    Code,
    HelpCircle,
    Puzzle,
    ShoppingBag,
    ShoppingBasket,
    Monitor,
    Layers,
    BarChart3
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { User } from '@supabase/supabase-js';
import { useEffect } from 'react';

import { useDashboard } from './DashboardContext';

interface SidebarProps {
    className?: string;
    onAction?: () => void;
}

export const Sidebar = ({ className, onAction }: SidebarProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { botName, avatarEmoji } = useDashboard();
    const [isImplementationsOpen, setIsImplementationsOpen] = useState(true);
    const [user, setUser] = useState<User | null>(null);

    useEffect(() => {
        supabase.auth.getUser().then(({ data }) => setUser(data.user)).catch(() => setUser(null));
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setUser(session?.user ?? null);
        });
        return () => subscription.unsubscribe();
    }, []);

    // Keep implementations open
    useEffect(() => {
        setIsImplementationsOpen(true);
    }, [location.pathname]);

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/');
    };

    const navItems = [
        { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard", id: "dashboard", isPrimary: true },
        { icon: StickyNote, label: "Knowledge", path: "/knowledge", id: "knowledge" },
        { icon: BarChart3, label: "Analytics", path: "/analytics", id: "analytics" },
        { icon: Puzzle, label: "Integrations", path: "/integrations", id: "integrations" },
        { icon: HelpCircle, label: "Contact", path: "/contact", id: "contact" },
    ];

    const guideItems = [
        { icon: Edit2, label: "Design", path: "/dashboard?step=customize", id: "appearance" },
        { icon: Database, label: "Add knowledge", path: "/dashboard?step=context", id: "context" },
        { icon: Rocket, label: "Deploy", path: "/dashboard?step=launch", id: "launch" },
    ];

    const implementationItems = [
        { icon: ShoppingBag, label: "Shopify", path: "/integrations?p=shopify", id: "shopify" },
        { icon: Globe, label: "WordPress", path: "/integrations?p=wordpress", id: "wordpress" },
        { icon: Layout, label: "Wix", path: "/integrations?p=wix", id: "wix" },
        { icon: Monitor, label: "Squarespace", path: "/integrations?p=squarespace", id: "squarespace" },
        { icon: Code, label: "Developer", path: "/integrations?p=custom", id: "custom" },
        { icon: Sparkles, label: "Ask AI", path: "/ai-implementation", id: "ai" },
    ];

    const searchParams = new URLSearchParams(location.search);
    const isWizardMode = searchParams.get('view') === 'create' ||
        searchParams.get('view') === 'edit' ||
        searchParams.get('step') !== null;

    return (
        <aside className={cn(
            "flex flex-col w-64 min-w-[16rem] h-full transition-all relative overflow-hidden shrink-0 font-dashboard",
            "bg-card/80 backdrop-blur-xl border-r border-border/60",
            className
        )}>
            {/* Background Ornaments */}
            <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-primary/5 to-transparent pointer-events-none" />

            {/* Header / Branding */}
            <div className="p-6 pb-2 relative">
                <Link to="/dashboard" className="flex items-center gap-3 group cursor-pointer" onClick={onAction}>
                    <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center shadow-lg shadow-primary/20 transform transition-transform duration-500 group-hover:rotate-12">
                        <img src="/assets/chytbot-mark-white-128.png" alt="" aria-hidden="true" className="w-6 h-6" />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-xl font-black tracking-tighter text-foreground uppercase leading-none group-hover:text-primary transition-colors">
                            ChytBot
                        </span>
                    </div>
                </Link>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-2 space-y-4 scrollbar-hide">
                {/* Dashboard Nav Group */}
                <div className="space-y-1">
                    {navItems.map((item) => {
                        const isActive = location.pathname === item.path;

                        return (
                            <Link
                                key={item.id}
                                to={item.path}
                                onClick={onAction}
                                className={cn(
                                    "flex items-center gap-3 w-full min-h-[42px] py-2.5 pl-4 pr-3 rounded-xl font-medium transition-all duration-200 group/item relative",
                                    "border-l-2 -ml-px pl-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                                    isActive
                                        ? "text-primary bg-primary/8 border-l-primary"
                                        : "text-muted-foreground border-l-transparent hover:text-foreground hover:bg-muted/80"
                                )}
                            >
                                <item.icon className={cn("w-5 h-5 flex-shrink-0 transition-colors", isActive ? "text-primary" : "text-muted-foreground group-hover/item:text-primary")} />
                                <span>{item.label}</span>
                            </Link>
                        );
                    })}
                </div>

                {/* Setup Guide Section - Conditional */}
                {isWizardMode && (
                    <div className="space-y-1 pt-4 border-t border-border animate-in fade-in slide-in-from-left-2 duration-300">
                        <h3 className="px-4 text-[10px] font-bold text-muted-foreground tracking-widest uppercase mb-2">
                            Creation steps
                        </h3>
                        {guideItems.map((item) => {
                            const itemStep = new URLSearchParams(item.path.split('?')[1]).get('step');
                            const currentStep = new URLSearchParams(location.search).get('step');
                            const isActive = itemStep === currentStep;

                            return (
                                <Link
                                    key={item.id}
                                    to={item.path}
                                    onClick={onAction}
                                    className={cn(
                                        "flex items-center justify-between w-full min-h-[42px] py-2.5 pl-4 pr-3 rounded-xl font-medium transition-all duration-200 group/item border-l-2 -ml-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                                        isActive
                                            ? "text-primary bg-primary/8 border-l-primary"
                                            : "text-muted-foreground border-l-transparent hover:text-foreground hover:bg-muted/80"
                                    )}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-5 h-5 flex items-center justify-center">
                                            <item.icon className={cn("w-4 h-4 flex-shrink-0 transition-colors", isActive ? "text-primary" : "text-muted-foreground group-hover/item:text-primary")} />
                                        </div>
                                        <span>{item.label}</span>
                                    </div>
                                    <div className={cn(
                                        "w-2 h-2 rounded-full border border-border flex items-center justify-center",
                                        isActive ? "bg-primary border-primary" : "bg-transparent group-hover/item:bg-primary/40"
                                    )} />
                                </Link>
                            );
                        })}
                    </div>
                )}

                {/* Implementations Collapsible */}
                <div className="space-y-1 pt-4 border-t border-border">
                    <div
                        className={cn(
                            "flex items-center justify-between w-full h-11 px-4 rounded-xl transition-all font-bold group/item",
                            "text-foreground bg-card/50"
                        )}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-5 h-5 flex items-center justify-center">
                                <Code className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <span className="text-[10px] tracking-[0.2em] uppercase">Integration guides</span>
                        </div>
                    </div>

                    {isImplementationsOpen && (
                        <div className="space-y-1 mt-1 animate-in slide-in-from-top-2 duration-200">
                            {implementationItems.map((item) => {
                                const currentParams = new URLSearchParams(location.search);
                                const itemParams = new URLSearchParams(item.path.split('?')[1]);
                                const isActive = location.pathname === '/integrations' && currentParams.get('p') === itemParams.get('p');

                                return (
                                    <Link
                                        key={item.id}
                                        to={item.path}
                                        onClick={onAction}
                                        className={cn(
                                            "flex items-center gap-3 w-full min-h-[40px] py-2 pl-10 pr-3 rounded-xl font-medium transition-all duration-200 text-xs group/sublink border-l-2 -ml-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                                            isActive
                                                ? "text-primary bg-primary/8 border-l-primary"
                                                : "text-muted-foreground border-l-transparent hover:text-foreground hover:bg-muted/80"
                                        )}
                                    >
                                        <item.icon className={cn("w-3.5 h-3.5 transition-colors", isActive ? "text-primary" : "text-muted-foreground group-hover/sublink:text-primary")} />
                                        <span>{item.label}</span>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* User chip + logout at bottom */}
            <div className="p-4 border-t border-border space-y-1">
                {user && (
                    <Link
                        to="/profile"
                        onClick={onAction}
                        className={cn(
                            "flex items-center gap-3 w-full min-h-[48px] py-2 px-3 rounded-xl transition-all duration-200 border-l-2 -ml-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                            location.pathname === '/profile'
                                ? "text-primary bg-primary/8 border-l-primary"
                                : "text-muted-foreground border-l-transparent hover:text-foreground hover:bg-muted/80"
                        )}
                    >
                        {user.user_metadata?.avatar_url ? (
                            <img
                                src={user.user_metadata.avatar_url as string}
                                alt=""
                                referrerPolicy="no-referrer"
                                className="w-9 h-9 rounded-full border border-border shrink-0"
                            />
                        ) : (
                            <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center shrink-0" aria-hidden="true">
                                <span className="text-sm font-bold text-primary-foreground">
                                    {((user.user_metadata?.full_name as string | undefined) || user.email || 'U').charAt(0).toUpperCase()}
                                </span>
                            </div>
                        )}
                        <div className="flex flex-col min-w-0 text-left">
                            <span className="text-sm font-medium text-foreground truncate">
                                {(user.user_metadata?.full_name as string | undefined) || 'Your profile'}
                            </span>
                            <span className="text-xs text-muted-foreground truncate">{user.email}</span>
                        </div>
                    </Link>
                )}
                <button
                    onClick={() => {
                        handleLogout();
                        onAction?.();
                    }}
                    className="flex items-center gap-3 w-full min-h-[42px] py-2.5 px-4 text-muted-foreground hover:text-destructive hover:bg-muted/80 transition-all duration-200 font-medium rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                    <LogOut className="w-5 h-5" />
                    <span>Log Out</span>
                </button>
            </div>
        </aside>
    );
};
